import { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Download,
  Upload,
  FileDown,
  FileUp,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Info,
} from 'lucide-react'
import api from '../../services/api'

// ── Types ─────────────────────────────────────────────────────────────────────

interface ModuleInfo {
  label: string
  importable: boolean
  description: string
}

interface ImportResult {
  success: boolean
  created: number
  updated: number
  skipped: number
  total_rows: number
  errors: string[]
}

interface AdminERP {
  id: number
  erp_name: string
  erp_type: string
  is_active: boolean | number
}

// ── Helper ─────────────────────────────────────────────────────────────────────

function Badge({ label, color }: { label: string; color: string }) {
  return (
    <span className={`inline-block px-2 py-0.5 text-xs rounded-full font-medium ${color}`}>
      {label}
    </span>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminMigration() {
  const { t } = useTranslation()

  const [modules, setModules] = useState<Record<string, ModuleInfo>>({})
  const [erps, setErps] = useState<AdminERP[]>([])
  const [loadingMods, setLoadMods] = useState(true)

  // Export form
  const [exportErp, setExportErp] = useState<number>(0)
  const [exportModule, setExportModule] = useState('')
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState('')

  // Import form
  const [importErp, setImportErp] = useState<number>(0)
  const [importModule, setImportModule] = useState('')
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importing, setImporting] = useState(false)
  const [importResult, setImportResult] = useState<ImportResult | null>(null)
  const [importError, setImportError] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Template download
  const [templateModule, setTemplateModule] = useState('')
  const [downloading, setDownloading] = useState(false)

  const load = async () => {
    setLoadMods(true)
    try {
      const [modsRes, erpsRes] = await Promise.all([api.getMigrationModules(), api.getAdminERPs()])
      setModules(modsRes.modules ?? {})
      const active = (Array.isArray(erpsRes) ? erpsRes : []).filter((e: AdminERP) => e.is_active)
      setErps(active)
      if (active.length > 0) {
        setExportErp(active[0].id)
        setImportErp(active[0].id)
      }
      const firstMod = Object.keys(modsRes.modules ?? {})[0] ?? ''
      setExportModule(firstMod)
      setTemplateModule(firstMod)
      const importable = Object.entries(modsRes.modules ?? {}).find(([, m]) => m.importable)
      setImportModule(importable ? importable[0] : firstMod)
    } catch (err: unknown) {
      console.error('Migration load error', err)
    } finally {
      setLoadMods(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  // ── Handlers ────────────────────────────────────────────────────────────────

  const handleExport = async () => {
    if (!exportErp || !exportModule) return
    setExporting(true)
    setExportError('')
    try {
      await api.downloadMigrationExport(exportErp, exportModule)
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        String(err)
      setExportError(msg)
    } finally {
      setExporting(false)
    }
  }

  const handleTemplate = async () => {
    if (!templateModule) return
    setDownloading(true)
    try {
      await api.downloadMigrationTemplate(templateModule)
    } catch (err: unknown) {
      console.error('Template download error', err)
    } finally {
      setDownloading(false)
    }
  }

  const handleImport = async () => {
    if (!importErp || !importModule || !importFile) return
    setImporting(true)
    setImportResult(null)
    setImportError('')
    try {
      const result = await api.importMigrationCsv(importErp, importModule, importFile)
      setImportResult(result)
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        String(err)
      setImportError(msg)
    } finally {
      setImporting(false)
    }
  }

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loadingMods) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
      </div>
    )
  }

  const moduleList = Object.entries(modules)
  const importableModules = moduleList.filter(([, m]) => m.importable)

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {t('migration.title', 'Migration ERP — Export / Import CSV')}
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t(
            'migration.subtitle',
            "Exportez les données d'un ERP au format CSV pivot, puis importez-les dans un autre ERP. Le format CSV est lisible et éditable dans Excel."
          )}
        </p>
      </div>

      {/* Module overview */}
      <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <h2 className="font-semibold text-gray-800 dark:text-white mb-3 flex items-center gap-2">
          <Info className="w-4 h-4 text-blue-500" />
          {t('migration.modules', 'Modules disponibles')}
        </h2>
        {/* Mobile card layout */}
        <ul className="divide-y divide-gray-100 dark:divide-gray-700 sm:hidden">
          {moduleList.map(([key, mod]) => (
            <li key={key} className="py-3 space-y-1">
              <div className="flex items-start justify-between gap-2">
                <p className="font-mono text-sm text-blue-700 dark:text-blue-400">{key}</p>
                <div className="flex gap-1.5 flex-wrap">
                  <Badge
                    label={t('migration.export', 'Export')}
                    color="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                  />
                  {mod.importable && (
                    <Badge
                      label={t('migration.import', 'Import')}
                      color="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                    />
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400">
                {mod.label} — {mod.description}
              </p>
            </li>
          ))}
        </ul>
        {/* Desktop table layout */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 dark:border-gray-700 text-left text-gray-500 dark:text-gray-400">
                <th className="pb-2 pr-4 font-medium">{t('migration.col.module', 'Module')}</th>
                <th className="pb-2 pr-4 font-medium">
                  {t('migration.col.description', 'Description')}
                </th>
                <th className="pb-2 font-medium">{t('migration.col.capabilities', 'Capacités')}</th>
              </tr>
            </thead>
            <tbody>
              {moduleList.map(([key, mod]) => (
                <tr key={key} className="border-b border-gray-50 dark:border-gray-700/50">
                  <td className="py-2 pr-4 font-mono text-blue-700 dark:text-blue-400">{key}</td>
                  <td className="py-2 pr-4 text-gray-700 dark:text-gray-300">
                    {mod.label} — {mod.description}
                  </td>
                  <td className="py-2 flex gap-2 flex-wrap">
                    <Badge
                      label={t('migration.export', 'Export')}
                      color="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                    />
                    {mod.importable && (
                      <Badge
                        label={t('migration.import', 'Import')}
                        color="bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Template download */}
      <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <h2 className="font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
          <FileDown className="w-4 h-4 text-purple-500" />
          {t('migration.template.title', 'Télécharger un modèle CSV vide')}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          {t(
            'migration.template.help',
            "Le modèle contient les en-têtes et une ligne d'exemple pour vous guider lors de la saisie dans Excel."
          )}
        </p>
        <div className="flex gap-3 items-end flex-wrap">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('migration.module_label', 'Module')}
            </label>
            <select
              value={templateModule}
              onChange={(e) => setTemplateModule(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              {moduleList.map(([key, mod]) => (
                <option key={key} value={key}>
                  {mod.label}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={handleTemplate}
            disabled={!templateModule || downloading}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
          >
            {downloading ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            {t('migration.template.btn', 'Télécharger le modèle')}
          </button>
        </div>
      </section>

      {/* Export */}
      <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <h2 className="font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
          <FileDown className="w-4 h-4 text-green-500" />
          {t('migration.export.title', "Exporter les données d'un ERP")}
        </h2>
        <div className="flex gap-4 items-end flex-wrap mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              ERP
            </label>
            <select
              value={exportErp}
              onChange={(e) => setExportErp(Number(e.target.value))}
              className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm min-w-48"
            >
              {erps.map((erp) => (
                <option key={erp.id} value={erp.id}>
                  {erp.erp_name} ({erp.erp_type})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('migration.module_label', 'Module')}
            </label>
            <select
              value={exportModule}
              onChange={(e) => setExportModule(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
            >
              {moduleList.map(([key, mod]) => (
                <option key={key} value={key}>
                  {mod.label}
                </option>
              ))}
            </select>
          </div>
          <button
            onClick={handleExport}
            disabled={!exportErp || !exportModule || exporting}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
          >
            {exporting ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            {t('migration.export.btn', 'Exporter en CSV')}
          </button>
        </div>
        {exportError && (
          <div className="flex items-start gap-2 text-red-600 dark:text-red-400 text-sm mt-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            {exportError}
          </div>
        )}
      </section>

      {/* Import */}
      <section className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <h2 className="font-semibold text-gray-800 dark:text-white mb-4 flex items-center gap-2">
          <FileUp className="w-4 h-4 text-blue-500" />
          {t('migration.import.title', 'Importer des données dans un ERP')}
        </h2>
        {importableModules.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {t('migration.import.none', "Aucun module ne supporte l'import dans cette version.")}
          </p>
        ) : (
          <>
            <div className="flex gap-4 items-end flex-wrap mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  ERP {t('migration.import.destination', '(destination)')}
                </label>
                <select
                  value={importErp}
                  onChange={(e) => setImportErp(Number(e.target.value))}
                  className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm min-w-48"
                >
                  {erps.map((erp) => (
                    <option key={erp.id} value={erp.id}>
                      {erp.erp_name} ({erp.erp_type})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('migration.module_label', 'Module')}
                </label>
                <select
                  value={importModule}
                  onChange={(e) => setImportModule(e.target.value)}
                  className="border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                >
                  {importableModules.map(([key, mod]) => (
                    <option key={key} value={key}>
                      {mod.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('migration.import.file', 'Fichier CSV')}
                </label>
                <div className="flex items-center gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    onChange={(e) => setImportFile(e.target.files?.[0] ?? null)}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-sm hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                  >
                    <Upload className="w-4 h-4" />
                    {importFile
                      ? importFile.name
                      : t('migration.import.choose', 'Choisir un fichier…')}
                  </button>
                </div>
              </div>
              <button
                onClick={handleImport}
                disabled={!importErp || !importModule || !importFile || importing}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-colors"
              >
                {importing ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4" />
                )}
                {t('migration.import.btn', 'Importer')}
              </button>
            </div>

            {importError && (
              <div className="flex items-start gap-2 text-red-600 dark:text-red-400 text-sm p-3 bg-red-50 dark:bg-red-900/20 rounded-lg mb-3">
                <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                {importError}
              </div>
            )}

            {importResult && (
              <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                <div className="flex items-center gap-2 text-green-700 dark:text-green-400 font-medium mb-3">
                  <CheckCircle className="w-4 h-4" />
                  {t('migration.import.done', 'Import terminé')} — {importResult.total_rows}{' '}
                  {t('migration.import.rows', 'lignes traitées')}
                </div>
                <div className="flex gap-6 text-sm flex-wrap">
                  <span className="text-green-700 dark:text-green-400">
                    <strong>{importResult.created}</strong> {t('migration.import.created', 'créés')}
                  </span>
                  <span className="text-blue-700 dark:text-blue-400">
                    <strong>{importResult.updated}</strong>{' '}
                    {t('migration.import.updated', 'mis à jour')}
                  </span>
                  <span className="text-gray-600 dark:text-gray-400">
                    <strong>{importResult.skipped}</strong>{' '}
                    {t('migration.import.skipped', 'ignorés')}
                  </span>
                </div>
                {importResult.errors.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-orange-700 dark:text-orange-400 mb-1">
                      {t('migration.import.warnings', 'Avertissements')} (
                      {importResult.errors.length})
                    </p>
                    <ul className="text-xs text-orange-600 dark:text-orange-400 space-y-0.5 max-h-40 overflow-y-auto">
                      {importResult.errors.map((e, i) => (
                        <li key={i}>• {e}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </section>
    </div>
  )
}
